/**
 * BarberNotificationPanel - Panel de notificaciones en tiempo real para barberos
 * QUEUE-008: Muestra notificaciones de eventos de cola (nuevos turnos, llegadas, etc.)
 */

import { useEffect, useState, useRef } from 'react';
import { Bell, X, User, UserCheck, UserX, Clock, Check, AlertTriangle } from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, deleteDoc, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import { useQueueSounds } from '../../hooks/useQueueSounds';
import { toast } from '@/lib/toast';
import { RejectTransferModal } from './RejectTransferModal';
import type { BarberNotification } from '../../types';

interface BarberNotificationPanelProps {
  barberId: string;
}

export default function BarberNotificationPanel({ barberId }: BarberNotificationPanelProps) {
  const [notifications, setNotifications] = useState<BarberNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { playSound } = useQueueSounds({ enableMonitoring: false });
  const shownNotificationsRef = useRef<Set<string>>(new Set());

  // Transfer rejection modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedTransferNotification, setSelectedTransferNotification] = useState<BarberNotification | null>(null);
  const [processingTransfer, setProcessingTransfer] = useState<string | null>(null);

  // Subscribe to notifications in real-time
  useEffect(() => {
    if (!barberId) return;

    let isMounted = true;

    const notificationsQuery = query(
      collection(db, 'barberNotifications'),
      where('barberId', '==', barberId),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      if (!isMounted) return;

      const newNotifications: BarberNotification[] = [];
      let newCount = 0;

      snapshot.docs.forEach((doc) => {
        const data = doc.data() as Omit<BarberNotification, 'notificationId'>;
        const notification: BarberNotification = {
          notificationId: doc.id,
          ...data,
        } as BarberNotification;

        newNotifications.push(notification);

        if (!notification.isRead) {
          newCount++;

          // Only show toast for truly NEW notifications (not shown before)
          if (!shownNotificationsRef.current.has(notification.notificationId)) {
            const soundType = notification.type === 'new_queue_ticket' ? 'notification' :
                             notification.type === 'client_arrived' ? 'success' : 'warning';
            playSound(soundType);

            // Show toast notification
            toast.info(notification.body);

            // Mark as shown
            shownNotificationsRef.current.add(notification.notificationId);
          }
        }
      });

      setNotifications(newNotifications);
      setUnreadCount(newCount);
    }, (error) => {
      console.error('Error listening to notifications:', error);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [barberId, playSound]);

  const markAsRead = async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'barberNotifications', notificationId), {
        isRead: true,
        readAt: new Date(),
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.isRead);

    try {
      await Promise.all(
        unreadNotifications.map(n =>
          updateDoc(doc(db, 'barberNotifications', n.notificationId), {
            isRead: true,
            readAt: new Date(),
            updatedAt: new Date(),
          })
        )
      );
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Error al marcar notificaciones');
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await deleteDoc(doc(db, 'barberNotifications', notificationId));
      setNotifications(prev => prev.filter(n => n.notificationId !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_queue_ticket':
        return <User className="w-5 h-5 text-ink-subtle" />;
      case 'client_arrived':
        return <UserCheck className="w-5 h-5 text-success" />;
      case 'client_cancelled':
      case 'client_no_show':
        return <UserX className="w-5 h-5 text-danger" />;
      case 'queue_empty':
        return <Clock className="w-5 h-5 text-ink-subtle" />;
      case 'transfer_requested':
        return <User className="w-5 h-5 text-accent" />;
      case 'transfer_accepted':
        return <UserCheck className="w-5 h-5 text-success" />;
      case 'transfer_rejected':
        return <UserX className="w-5 h-5 text-danger" />;
      case 'transfer_expired':
        return <Clock className="w-5 h-5 text-warning" />;
      // Overtime notifications
      case 'overtime_requested':
        return <AlertTriangle className="w-5 h-5 text-warning" />;
      case 'overtime_accepted':
        return <UserCheck className="w-5 h-5 text-success" />;
      case 'overtime_rejected':
        return <UserX className="w-5 h-5 text-danger" />;
      case 'overtime_expired':
        return <Clock className="w-5 h-5 text-ink-subtle" />;
      default:
        return <Bell className="w-5 h-5 text-ink-subtle" />;
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return date.toLocaleDateString();
  };

  // Handle accept transfer
  const handleAcceptTransfer = async (notification: BarberNotification) => {
    if (!notification.data?.queueId) return;

    setProcessingTransfer(notification.notificationId);

    try {
      const acceptTransferFn = httpsCallable<{ queueId: string }, { success: boolean; message: string }>(
        functions,
        'acceptTransfer'
      );

      const result = await acceptTransferFn({ queueId: notification.data.queueId });

      if (result.data.success) {
        toast.success('Traspaso aceptado correctamente');
        await markAsRead(notification.notificationId);
      } else {
        toast.error(result.data.message || 'Error al aceptar traspaso');
      }
    } catch (error: any) {
      console.error('Error accepting transfer:', error);
      toast.error(error.message || 'Error al aceptar traspaso');
    } finally {
      setProcessingTransfer(null);
    }
  };

  // Handle reject transfer
  const handleRejectTransfer = (notification: BarberNotification) => {
    setSelectedTransferNotification(notification);
    setRejectModalOpen(true);
  };

  // Handle reject transfer success
  const handleRejectSuccess = async () => {
    if (selectedTransferNotification) {
      await markAsRead(selectedTransferNotification.notificationId);
      setSelectedTransferNotification(null);
    }
  };

  return (
    <>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-3 rounded-full bg-surface border border-hairline hover:bg-surface-2 transition-colors"
      >
        <Bell className="w-6 h-6 text-ink-muted" />
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 bg-accent rounded-full flex items-center justify-center">
            <span className="text-xs font-bold text-on-accent tabular-nums">{unreadCount > 9 ? '9+' : unreadCount}</span>
          </div>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:relative lg:inset-auto">
          {/* Mobile Overlay */}
          <div className="lg:hidden fixed inset-0 bg-black/60" onClick={() => setIsOpen(false)} />

          {/* Panel */}
          <div className="fixed lg:absolute top-0 right-0 bottom-0 lg:bottom-auto lg:top-full lg:right-0 mt-2
                         w-full max-w-md lg:max-w-sm bg-surface
                         shadow-2xl lg:rounded-2xl border-l lg:border border-hairline
                         flex flex-col max-h-screen lg:max-h-[600px]">

            {/* Header */}
            <div className="p-4 border-b border-hairline flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-ink-subtle" />
                <h3 className="font-sans font-semibold text-ink">Notificaciones</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-accent/10 text-accent border border-accent/30 text-xs font-bold rounded-full tabular-nums">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-accent hover:underline"
                  >
                    Marcar todas
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-10 h-10 text-ink-subtle mx-auto mb-3" />
                  <p className="text-ink-muted">No hay notificaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-hairline">
                  {notifications.map((notification) => (
                    <div
                      key={notification.notificationId}
                      className={`p-4 hover:bg-surface-2 transition-colors ${
                        !notification.isRead ? 'bg-accent/5' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notification.isRead ? 'font-semibold' : ''} text-ink`}>
                            {notification.body}
                          </p>
                          {/* Show rejection reason if present */}
                          {notification.type === 'transfer_rejected' && notification.data?.transferRejectionNotes && (
                            <p className="text-xs text-ink-muted mt-1 italic">
                              "{notification.data.transferRejectionNotes}"
                            </p>
                          )}
                          <p className="text-xs text-ink-subtle mt-1">
                            {formatTime(notification.createdAt)}
                          </p>
                          {/* Transfer requested actions */}
                          {notification.type === 'transfer_requested' && !notification.isRead && (
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => handleAcceptTransfer(notification)}
                                disabled={processingTransfer === notification.notificationId}
                                className="flex-1 px-3 py-1.5 bg-accent text-on-accent text-xs font-medium rounded hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                              >
                                {processingTransfer === notification.notificationId ? (
                                  <>
                                    <div className="w-3 h-3 border-2 border-on-accent border-t-transparent rounded-full animate-spin" />
                                    <span>Aceptando…</span>
                                  </>
                                ) : (
                                  <>
                                    <Check className="w-3 h-3" />
                                    <span>Aceptar</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => handleRejectTransfer(notification)}
                                disabled={processingTransfer === notification.notificationId}
                                className="flex-1 px-3 py-1.5 bg-transparent border border-hairline text-danger text-xs font-medium rounded hover:bg-danger/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                              >
                                <X className="w-3 h-3" />
                                <span>Rechazar</span>
                              </button>
                            </div>
                          )}
                        </div>
                        {notification.type !== 'transfer_requested' && (
                          <button
                            onClick={() => {
                              if (!notification.isRead) markAsRead(notification.notificationId);
                              else deleteNotification(notification.notificationId);
                            }}
                            className="flex-shrink-0 p-1 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Transfer Modal */}
      {selectedTransferNotification && (
        <RejectTransferModal
          queueId={selectedTransferNotification.data?.queueId || ''}
          clientName={selectedTransferNotification.data?.clientName || 'Cliente'}
          fromBarberName={selectedTransferNotification.data?.transferFromBarberName || 'Barbero'}
          isOpen={rejectModalOpen}
          onClose={() => {
            setRejectModalOpen(false);
            setSelectedTransferNotification(null);
          }}
          onSuccess={handleRejectSuccess}
        />
      )}
    </>
  );
}
